import type { Artifact, MatrixCoverage } from '../types.ts';
import { formatCost } from '../lib/artifact.ts';

/** Coverage strip status → legend-key class: settled green for done, hot for failed (a dead
 * crawl is a hole in the survey, not a finding about the vendor — but it must not read calm). */
const STATUS_KEY: Record<'done' | 'failed', string> = { done: 'k-present', failed: 'k-absent' };

const pctOf = (c: MatrixCoverage | { pages_read: number; pages_known: number }): number =>
  c.pages_known > 0 ? Math.min(100, Math.round((c.pages_read / c.pages_known) * 100)) : 0;

/**
 * Per-competitor coverage: the run's line (status, pages crawled, cost) merged with the
 * artifact's per-vendor coverage (pages read/known), joined by slug. Any vendor without a run
 * record still gets a coverage-only card so nothing is lost either way.
 *
 * A competitor that failed has no vendor block, but the run still recorded what it managed to
 * read before it died, and `matrix.coverage` is the only place that figure survives
 * (docs/specs/degraded-runs.md). So coverage falls back to the matrix row, joined by company —
 * showing nothing would suppress a number we hold, which is its own small untruth, and "it read
 * 7 pages and none of it became a record" is a materially different fact from "it read nothing".
 *
 * `showCost` is false for a non-admin viewer, who never sees a dollar figure
 * (docs/specs/accounts.md, section 5). The figure goes whole, separator included.
 */
export function CoverageStrip({
  artifact,
  showCost,
}: {
  artifact: Artifact;
  showCost: boolean;
}): JSX.Element {
  const competitorSlugs = new Set(artifact.run.competitors.map((c) => c.slug));
  const coverageOnlyVendors = artifact.vendors.filter((v) => !competitorSlugs.has(v.slug));

  return (
    <div className="hairline-stack coverage-strip">
      {artifact.run.competitors.map((c) => {
        const vendor = artifact.vendors.find((v) => v.slug === c.slug);
        const coverage =
          vendor?.coverage ?? artifact.matrix.coverage.find((x) => x.company === c.company) ?? null;
        // No record survived for this competitor, whatever it read on the way down.
        const unassessed = !vendor;
        return (
          <div key={c.slug} className="coverage-card">
            <div className="coverage-card-top">
              <span className="coverage-name">{c.company}</span>
              <span className={`key ${STATUS_KEY[c.status]}`}>{c.status}</span>
            </div>
            {/* No bar for an unassessed vendor. Its pages_known IS its pages_read — the run never
                learned of a wider corpus — so a bar would sit at 100% and read as full coverage,
                claiming the exact opposite of what happened. The figures still show below. */}
            {coverage && !unassessed && (
              <div className="coverage-bar">
                <div
                  className={`coverage-fill${pctOf(coverage) >= 100 ? ' full' : ''}`}
                  style={{ width: `${pctOf(coverage)}%` }}
                />
              </div>
            )}
            <span className="coverage-meta">
              {coverage && `${coverage.pages_read}/${coverage.pages_known} pages read · `}
              {c.pages_crawled} crawled
              {showCost && ` · ${formatCost(c.cost_usd)}`}
              {unassessed && <span className="coverage-unassessed"> · no usable record</span>}
            </span>
          </div>
        );
      })}
      {coverageOnlyVendors.map((v) => (
        <div key={v.slug} className="coverage-card">
          <div className="coverage-card-top">
            <span className="coverage-name">{v.company}</span>
          </div>
          <div className="coverage-bar">
            <div
              className={`coverage-fill${pctOf(v.coverage) >= 100 ? ' full' : ''}`}
              style={{ width: `${pctOf(v.coverage)}%` }}
            />
          </div>
          <span className="coverage-meta">
            {v.coverage.pages_read}/{v.coverage.pages_known} pages read
          </span>
        </div>
      ))}
    </div>
  );
}
